import { Component } from '@opten/gin-tonic/helpers/compiler';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

@Component({ className: 'js-cards-trigger' })
export class CardsComponent {
	private spacer: HTMLDivElement | null = null;
	private sectionHeader: HTMLElement | null | undefined = null;
	private initialHeaderMarginTop = 0;
	private initialHeaderMarginBottom = 0;
	private mm = gsap.matchMedia();
	// keep references to listeners for cleanup
	private updateSpacerRef?: () => void;
	constructor(private element: HTMLElement) {
		this.mm.add('(min-width: 1200px)', () => {
			const container = document.querySelector<HTMLElement>('.c-card__section');
			const text = container?.querySelector('.c-section__subtitle');
			const sectionHeader =
				container?.querySelector<HTMLElement>('.c-section__header');
			const items = Array.from(container?.querySelectorAll('.js-card') ?? []);
			const sectionTitle =
				container?.querySelector<HTMLElement>('.c-section__title');

			const itemsHeight = items?.reduce((total, item) => {
				const title = item.querySelector<HTMLElement>('.h3');
				const itemHeight = title?.offsetHeight || 0;
				return total + itemHeight + 39;
			}, 0);

			let height = 200 + (sectionTitle?.offsetHeight || 0) + itemsHeight;

			container?.style.setProperty('height', `${height * 1.1}px`);

			const tlIn = gsap.timeline({
				scrollTrigger: {
					trigger: sectionHeader,
					start: 'top-=100px bottom',
					end: 'center-=100px center',
					scrub: true,
				},
			});
			if (sectionTitle)
				tlIn.fromTo(
					sectionTitle,
					{ y: 20, opacity: 0, scale: 0.8 },
					{ y: 0, opacity: 1, scale: 1 }
				);
			if (text)
				tlIn.fromTo(
					text,
					{ y: 20, opacity: 0, scale: 0.8 },
					{ y: 0, opacity: 1, scale: 1.0 },
					'<0.2'
				);

			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: container,
					pin: true,
					start: 'top top',
					end: 'bottom top',
					// markers: true,
					scrub: 1,
					pinSpacing: true,
				},
			});

			tl.fromTo(
				text!,
				{ y: 0, opacity: 1, scale: 1.0 },
				{ y: 0, height: '0px', opacity: 0, scale: 1 }
			);

			items?.forEach((item, index) => {
				tl.to(
					item.querySelector('.js-card-text')!,
					{ height: 0, opacity: 0, ease: 'power1.out' },
					'+=0.01'
				);
				tl.to(item, { marginBottom: 0 }, '<');
			});

			const header = container?.parentElement?.nextElementSibling;
			const pinSpacer = this.element.closest(
				'.pin-spacer'
			) as HTMLElement | null;

			// If a pin-spacer exists, include its bottom padding in the
			// ScrollTrigger offsets so the pinned area accounts for that spacing.
			const spacerPaddingBottom = pinSpacer
				? parseFloat(
						pinSpacer.style.paddingBottom &&
							pinSpacer.style.paddingBottom != '0px'
							? pinSpacer.style.paddingBottom
							: pinSpacer.style.height || '0'
				  ) || 0
				: 0;
			// Comment MZA: Seems to be height initially, then padding..
			// Might need to observe changes like resize?

			const tl2 = gsap.timeline({
				scrollTrigger: {
					trigger: header,
					// add the spacer bottom padding to both offsets
					start: `top-=${spacerPaddingBottom}px top`,
					end: `top-=${spacerPaddingBottom}px top-=150px`,
					scrub: 1,
					// markers: true,
				},
			});

			tl2.fromTo(
				sectionTitle!,
				{ y: 0, opacity: 1, scale: 1 },
				{ y: -40, opacity: 0, scale: 0.95 }
			);

			return () => {
				// Kill ScrollTriggers that target this component
				ScrollTrigger.getAll().forEach((st) => {
					const trg = (st as any).trigger ?? (st as any).vars?.trigger;
					const contains = (node: any) =>
						node instanceof Element &&
						(node === this.element || this.element.contains(node));
					if (trg instanceof Element) {
						if (contains(trg)) st.kill();
					} else if (trg && typeof (trg as any).forEach === 'function') {
						let shouldKill = false;
						(trg as any).forEach((n: any) => {
							if (contains(n)) shouldKill = true;
						});
						if (shouldKill) st.kill();
					}
				});

				// Remove listeners
				if (this.updateSpacerRef) {
					ScrollTrigger.removeEventListener(
						'refreshInit',
						this.updateSpacerRef
					);
					ScrollTrigger.removeEventListener('refresh', this.updateSpacerRef);
					window.removeEventListener('resize', this.updateSpacerRef);
					document.removeEventListener(
						'aos:refresh',
						this.updateSpacerRef as any
					);
					this.updateSpacerRef = undefined;
				}

				// Remove spacer and restore parent's margin
				if (this.spacer) {
					const parentEl = this.element.parentElement as HTMLElement | null;
					this.spacer.remove();
					this.spacer = null;
					if (parentEl) {
						parentEl.style.marginBottom = '';
					}
				}

				// Restore header styles
				if (this.sectionHeader) {
					this.sectionHeader.style.position = '';
					this.sectionHeader.style.marginTop = '';
					this.sectionHeader.style.marginBottom = '';
				}

				// Reset section height
				if (container) {
					container.style.height = '';
				}

				// Clear GSAP-managed styles from cards/text
				const clear = (
					els: NodeListOf<Element> | Element[] | null | undefined
				) => {
					if (!els) return;
					const arr =
						els instanceof NodeList ? Array.from(els) : (els as Element[]);
					arr.forEach((el) => {
						gsap.killTweensOf(el);
						gsap.set(el as any, {
							clearProps:
								'x,y,opacity,scale,rotate,transform,filter,clipPath,height,width,marginTop,marginBottom,boxShadow,willChange,paddingBottom',
						});
					});
				};
				clear(this.element.querySelectorAll('[data-gsap-aos="zoom-in-up"]'));
				clear(this.element.querySelectorAll('.js-card'));
				clear(this.element.querySelectorAll('.js-card .js-card-text'));
			};
		});
	}
}
